import { Injectable } from '@angular/core';
import { AuthService } from '../../auth.service';
import { AlertDialogService } from '../alert-dialog/alert-dialog.service';
import { SharedService } from '../shared.service';

const GUARDIAN_ACCESS_REQUEST_PATH = '/dashboard/request';

@Injectable({
  providedIn: 'root',
})
export class AuthzAccessHintService {
  private dashboardConfigPromise: Promise<{ guardianDashboardBaseUrl?: string } | null> | null = null;

  constructor(
    private auth: AuthService,
    private sharedService: SharedService,
    private alertDialog: AlertDialogService,
  ) {}

  // Show popup only when user lacks permission for the feature.
  async showAccessDeniedDialogForFeaturePanel(featureId?: string): Promise<void> {
    if (!featureId) {
      return;
    }

    // Check if feature is in authzPanels (from config)
    const authzPanels = this.auth.getAuthzPanels();
    if (!authzPanels.includes(featureId)) {
      return;
    }

    const guardianBaseUrl = await this.getGuardianDashboardBaseUrl();
    const requestUrl = guardianBaseUrl ? `${guardianBaseUrl}${GUARDIAN_ACCESS_REQUEST_PATH}` : '';

    const message = requestUrl
      ? `Access for ${featureId} is managed in Guardian. Use the link below to request access.`
      : `Access for ${featureId} is managed in Guardian.`;

    this.alertDialog.open({
      title: 'Access Denied',
      message,
      confirmText: 'OK',
      links: requestUrl
        ? [{ label: requestUrl, url: requestUrl }]
        : undefined,
    });

    // Wait for dialog to close before returning
    await this.alertDialog.confirmed().toPromise();
  }

  // Get access hint data for footer display.
  async getAccessHintData(featureId?: string): Promise<{ message: string; requestUrl: string } | null> {
    if (!featureId) {
      return null;
    }

    // Check if feature is in authzPanels (from config)
    const authzPanels = this.auth.getAuthzPanels();
    if (!authzPanels.includes(featureId)) {
      return null;
    }

    const guardianBaseUrl = await this.getGuardianDashboardBaseUrl();
    const requestUrl = guardianBaseUrl ? `${guardianBaseUrl}${GUARDIAN_ACCESS_REQUEST_PATH}` : '';

    return {
      message: 'Access for this panel is managed in Guardian.',
      requestUrl,
    };
  }

  private async getGuardianDashboardBaseUrl(): Promise<string> {
    const config = await this.loadDashboardConfig();
    return (config && config.guardianDashboardBaseUrl) || '';
  }

  private loadDashboardConfig(): Promise<{ guardianDashboardBaseUrl?: string } | null> {
    if (!this.dashboardConfigPromise) {
      this.dashboardConfigPromise = this.sharedService.fetchDashboardConfig()
        .toPromise()
        .then((res) => this.sharedService.parseDashboardConfigResponse(res))
        .catch(() => null);
    }
    return this.dashboardConfigPromise;
  }
}
